import { useEffect, useState } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from '@/components/layouts/AdminLayout';
import { addCredits } from '@/services/creditService';

interface Organization {
  id: string;
  name: string;
  remaining_credits: number;
}

const CreditManagement = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [creditAmount, setCreditAmount] = useState<number>(10);
  const [description, setDescription] = useState<string>('Manual credit adjustment');
  const [transactionType, setTransactionType] = useState<'subscription' | 'bonus' | 'refund' | 'adjustment'>('adjustment');
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(false);

  // Search organizations when searchTerm changes
  useEffect(() => {
    const searchOrganizations = async () => {
      if (!searchTerm.trim()) {
        setOrganizations([]);
        return;
      }

      setLoadingOrgs(true);
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select(`
            id,
            name,
            credits!inner (
              remaining_credits
            )
          `)
          .ilike('name', `%${searchTerm}%`)
          .limit(10);

        if (error) throw error;
        
        const formattedData = data?.map(org => ({
          id: org.id,
          name: org.name,
          remaining_credits: org.credits?.[0]?.remaining_credits || 0
        })) || [];
        
        setOrganizations(formattedData);
      } catch (error) {
        console.error('Error searching organizations:', error);
        toast.error('Failed to search organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };

    const debounce = setTimeout(searchOrganizations, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) {
      toast.error('Please select an organization');
      return;
    }

    setLoading(true);
    try {
      await addCredits(
        selectedOrg,
        creditAmount,
        description,
        transactionType
      );

      toast.success('Credits updated successfully');
      setSelectedOrg('');
      setSearchTerm('');
      setCreditAmount(10);
      setDescription('Manual credit adjustment');
    } catch (error: any) {
      console.error('Error updating credits:', error);
      toast.error(error.message || 'Failed to update credits');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-6">Credit Management</h1>

        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label>Organization</Label>
            <div className="relative">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations..."
                className="w-full"
              />

              {searchTerm && !selectedOrg && (
                <div className="absolute w-full mt-1 bg-popover border rounded-md shadow-lg z-50">
                  {loadingOrgs ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      <span className="text-sm text-muted-foreground">Searching...</span>
                    </div>
                  ) : organizations.length > 0 ? (
                    <div className="py-1">
                      {organizations.map((org) => (
                        <div
                          key={org.id}
                          className={`px-4 py-2 cursor-pointer hover:bg-accent ${
                            selectedOrg === org.id ? 'bg-accent' : ''
                          }`}
                          onClick={() => {
                            setSelectedOrg(org.id);
                            setSearchTerm(org.name);
                          }}
                        >
                          <div>{org.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Current Credits: {org.remaining_credits}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No organizations found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Credit Amount</Label>
            <Input
              type="number"
              value={creditAmount}
              onChange={(e) => setCreditAmount(Number(e.target.value))}
              min={1}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Transaction Type</Label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value as any)}
              className="w-full p-2 border rounded"
            >
              <option value="adjustment">Adjustment</option>
              <option value="bonus">Bonus</option>
              <option value="refund">Refund</option>
              <option value="subscription">Subscription</option>
            </select>
          </div>

          <Button type="submit" disabled={loading || !selectedOrg}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Credits'
            )}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default CreditManagement;